import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { SessionData } from '@gpa-scales/shared-types';

const DB_NAME = 'gpa-scales-snapshots';
const DB_VERSION = 1;
const STORE_NAME = 'snapshots';

interface SnapshotRecord {
  id: string;
  data: SessionData;
  label: string;
  createdAt: string;
}

interface GpaScalesDB extends DBSchema {
  snapshots: {
    key: string;
    value: SnapshotRecord;
    indexes: { 'by-date': string };
  };
}

let dbInstance: IDBPDatabase<GpaScalesDB> | null = null;

async function getDB(): Promise<IDBPDatabase<GpaScalesDB>> {
  if (dbInstance) return dbInstance;
  dbInstance = await openDB<GpaScalesDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      store.createIndex('by-date', 'createdAt');
    },
  });
  return dbInstance;
}

/**
 * Generate a unique snapshot ID.
 */
function generateSnapshotId(): string {
  return `snap_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Save a snapshot of the current session to IndexedDB.
 */
export async function saveSnapshot(data: SessionData, label?: string): Promise<string> {
  const db = await getDB();
  const id = generateSnapshotId();
  const record: SnapshotRecord = {
    id,
    data,
    label: label ?? `Snapshot ${new Date().toLocaleString()}`,
    createdAt: new Date().toISOString(),
  };
  await db.put(STORE_NAME, record);
  return id;
}

/**
 * List all saved snapshots, ordered by creation date (newest first).
 */
export async function listSnapshots(): Promise<SnapshotRecord[]> {
  try {
    const db = await getDB();
    const all = await db.getAll(STORE_NAME);
    return all.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  } catch {
    return [];
  }
}

/**
 * Load a specific snapshot by ID.
 */
export async function loadSnapshot(id: string): Promise<SessionData | null> {
  try {
    const db = await getDB();
    const record = await db.get(STORE_NAME, id);
    return record?.data ?? null;
  } catch {
    return null;
  }
}

/**
 * Delete a snapshot by ID.
 */
export async function deleteSnapshot(id: string): Promise<void> {
  const db = await getDB();
  await db.delete(STORE_NAME, id);
}

/**
 * Compare two snapshots and return the differences.
 */
export async function compareSnapshots(
  id1: string,
  id2: string
): Promise<{
  snapshot1: SessionData | null;
  snapshot2: SessionData | null;
  courseCountDiff: number;
  settingsChanged: boolean;
  label1: string;
  label2: string;
} | null> {
  try {
    const db = await getDB();
    const rec1 = await db.get(STORE_NAME, id1);
    const rec2 = await db.get(STORE_NAME, id2);
    if (!rec1 || !rec2) return null;

    return {
      snapshot1: rec1.data,
      snapshot2: rec2.data,
      courseCountDiff: rec2.data.courses.length - rec1.data.courses.length,
      settingsChanged: JSON.stringify(rec1.data.settings) !== JSON.stringify(rec2.data.settings),
      label1: rec1.label,
      label2: rec2.label,
    };
  } catch {
    return null;
  }
}

/**
 * Check if IndexedDB is available.
 */
export async function isIndexedDBAvailable(): Promise<boolean> {
  try {
    await getDB();
    return true;
  } catch {
    return false;
  }
}
